import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import { QuotesListView } from '../quotation/QuotesListView'
import { QuotationCreateView } from '../quotation/QuotationCreateView'
import { QuotationLinesView } from '../quotation/QuotationLinesView'
import { Layout } from '../ui/Layout'
import { dataService } from '../../services/DataService'
import { apiService } from '../../services/ApiService'
import { useBackendAuth } from '../auth/BackendAuthProvider'
import { getQuotationDataFromUrl, isQuotationCreateRequest } from '../../js/utils/QuotationUrlParams'

export const SimpleDashboard = ({ initialView = 'overview', embedded = false, hideEmailFilter = false }) => {
  const { t } = useTranslation('dashboard')
  const { dataVersion } = useBackendAuth()
  const [activeView, setActiveView] = useState(initialView)
  const [isCreating, setIsCreating] = useState(false)
  // Embedded create flow: quotation was created but its details could not be read back yet
  const [createdWithoutDetails, setCreatedWithoutDetails] = useState(false)
  const [selectedQuotationNumber, setSelectedQuotationNumber] = useState(null)
  const [prefilledData, setPrefilledData] = useState(null)
  const [totalQuotations, setTotalQuotations] = useState(null)
  const [openQuotations, setOpenQuotations] = useState(null)
  const [followupThisWeek, setFollowupThisWeek] = useState(null)
  const [expiryThisWeek, setExpiryThisWeek] = useState(null)
  const [followupPastDue, setFollowupPastDue] = useState(null)
  const [expiryPastDue, setExpiryPastDue] = useState(null)
  const [isLoadingCount, setIsLoadingCount] = useState(true)
  const [targetListView, setTargetListView] = useState(null)

  // Fetch quotations counts when component mounts or returns to overview
  useEffect(() => {
    const fetchQuotationsCounts = async () => {
      if (activeView === 'overview') {
        setIsLoadingCount(true)
        try {
          // Fetch all counts in parallel for maximum performance
          const [totalResult, openResult, followupResult, expiryResult, followupPastDueResult, expiryPastDueResult] = await Promise.all([
            apiService.getQuotationsCount(),
            apiService.getOpenQuotationsCount(),
            apiService.getFollowupThisWeekCount(),
            apiService.getExpiryThisWeekCount(),
            apiService.getFollowupPastDueCount(),
            apiService.getExpiryPastDueCount()
          ])

          if (totalResult.success) {
            setTotalQuotations(totalResult.count)
          } else {
            console.error('Failed to fetch total quotations count:', totalResult.error)
          }

          if (openResult.success) {
            setOpenQuotations(openResult.count)
          } else {
            console.error('Failed to fetch open quotations count:', openResult.error)
          }

          if (followupResult.success) {
            setFollowupThisWeek(followupResult.count)
          } else {
            console.error('Failed to fetch follow-up this week count:', followupResult.error)
          }

          if (expiryResult.success) {
            setExpiryThisWeek(expiryResult.count)
          } else {
            console.error('Failed to fetch expiry this week count:', expiryResult.error)
          }

          if (followupPastDueResult.success) {
            setFollowupPastDue(followupPastDueResult.count)
          } else {
            console.error('Failed to fetch follow-up past due count:', followupPastDueResult.error)
          }

          if (expiryPastDueResult.success) {
            setExpiryPastDue(expiryPastDueResult.count)
          } else {
            console.error('Failed to fetch expiry past due count:', expiryPastDueResult.error)
          }
        } catch (error) {
          console.error('Error fetching quotations counts:', error)
        } finally {
          setIsLoadingCount(false)
        }
      }
    }

    fetchQuotationsCounts()
  }, [activeView, dataVersion])

  // Open the create view from a direct URL or after authentication restores its query params.
  useEffect(() => {
    const applyUrlState = () => {
      if (initialView === 'create' || (initialView !== 'list' && isQuotationCreateRequest())) {
        const urlData = getQuotationDataFromUrl()
        setPrefilledData(Object.keys(urlData).length > 0 ? urlData : null)
        setActiveView('create')
        if (!embedded) {
          window.history.replaceState({}, document.title, window.location.pathname)
        }
        return
      }

      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.has('opportunity')) {
        setActiveView('list')
      }
    }

    applyUrlState()
    const handleUrlRestored = () => applyUrlState()
    window.addEventListener('urlRestored', handleUrlRestored)

    return () => window.removeEventListener('urlRestored', handleUrlRestored)
  }, [])

  const handleCreateQuotation = async (formData) => {
    setIsCreating(true)
    try {
      console.log('Creating quotation:', formData)
      
      // Log customer validation result
      if (formData.isExistingCustomer) {
        console.log(`Customer account "${formData.custAccount}" found in customer list - using mserp_requestingcustomeraccountnumber`)
      } else {
        console.log(`Customer account "${formData.custAccount}" not found in customer list - using mserp_prospectid`)
      }
      
      // Prepare quotation data for API
      // Use prospectId if customer doesn't exist, otherwise use customer account
      const quotationData = {
        mserp_salesquotationname: formData.isNewCustomerOrProspect ? formData.quotationName : formData.customerName,
        mserp_alg_salesquotationname: formData.quotationName,
        // The form only exposes this value when automatic numbering is disabled.
        ...(formData.quotationNumber && { mserp_salesquotationnumber: formData.quotationNumber }),
        // If customer exists, use customer account field; otherwise use prospect field
        ...(formData.isExistingCustomer
          ? { mserp_requestingcustomeraccountnumber: formData.custAccount }
          : { mserp_prospectid: formData.custAccount }
        ),
        // Contact name if provided
        ...(formData.contactName && { mserp_alg_salesportalcontactname: formData.contactName }),
        // Contact email if provided
        ...(formData.contactEmail && { mserp_alg_salesportalcontactemail: formData.contactEmail }),
        // Follow up date if provided
        ...(formData.followUpDate && { mserp_salesquotationfollowupdate: new Date(formData.followUpDate).toISOString() }),
        // Expiry date if provided
        ...(formData.expiryDate && { mserp_salesquotationexpirydate: new Date(formData.expiryDate).toISOString() }),
        // Opportunity ID if provided
        ...(formData.opportunityId && { mserp_alg_portalopportunity: formData.opportunityId }),
        // Set initial status to Created
        mserp_salesquotationstatus: 200000000
        // Note: mserp_dataareaid will be set by backend based on authenticated user's company
      }
      
      // Note: Backend will automatically set the correct company (e.g., 999)
      // based on the authenticated user's Dataverse record

      // Log quotation number handling
      if (formData.quotationNumber) {
        console.log(`Using manual quotation number: ${formData.quotationNumber}`)
      } else {
        console.log('No quotation number provided - Dataverse will auto-generate')
      }

      console.log('Sending quotation data to API:', quotationData)
      const result = await dataService.createQuotation(quotationData)
      
      if (result.success) {
        console.log('Quotation created successfully:', result.data)

        // Backend handles retries and returns quotation data with the number
        if (result.data && result.data.mserp_salesquotationnumber) {
          // Extract quotation number from the created quotation
          const createdQuotationNumber = result.data.mserp_salesquotationnumber
          console.log('Opening quotation lines for:', createdQuotationNumber)

          // Open the details of the newly created quotation.
          // In embedded mode (/create-quotation) this becomes the only view: the user
          // can work on this quotation but never reaches the full quotation list.
          setSelectedQuotationNumber(createdQuotationNumber)
          // Stay in create view, but modal will open
        } else {
          // Fallback if backend couldn't retrieve the quotation after retries
          console.log('⚠️ Quotation created but could not retrieve quotation number')
          if (embedded) {
            // Never fall back to the quotation list in the embedded flow
            setCreatedWithoutDetails(true)
          } else {
            console.log('Returning to overview - user can find the quotation in the list')
            setActiveView('overview')
          }
        }
      } else {
        console.error('Failed to create quotation:', result.error)
        // Throw error so QuotationForm's catch block can handle it
        throw new Error(result.error || 'Failed to create quotation')
      }
    } catch (error) {
      console.error('Error creating quotation:', error)
      // Re-throw error so QuotationForm can handle it
      throw error
    } finally {
      setIsCreating(false)
    }
  }

  const handleStatClick = (targetView) => {
    if (targetView) {
      setTargetListView(targetView)
      setActiveView('list')
    }
  }

  const stats = [
    {
      label: t('stats.totalQuotations'),
      value: isLoadingCount ? '...' : (totalQuotations !== null ? totalQuotations.toString() : '0'),
      change: null,
      pastDue: null,
      clickable: false
    },
    {
      label: t('stats.activeQuotes'),
      value: isLoadingCount ? '...' : (openQuotations !== null ? openQuotations.toString() : '0'),
      change: null,
      pastDue: null,
      clickable: true,
      targetView: 'open'
    },
    {
      label: t('stats.followUp'),
      value: isLoadingCount ? '...' : (followupThisWeek !== null ? followupThisWeek.toString() : '0'),
      change: null,
      pastDue: isLoadingCount ? null : (followupPastDue !== null ? followupPastDue : 0),
      pastDueLabel: 'stats.followUpPastDue',
      clickable: true,
      targetView: 'followup'
    },
    {
      label: t('stats.expiry'),
      value: isLoadingCount ? '...' : (expiryThisWeek !== null ? expiryThisWeek.toString() : '0'),
      change: null,
      pastDue: isLoadingCount ? null : (expiryPastDue !== null ? expiryPastDue : 0),
      pastDueLabel: 'stats.expiryPastDue',
      clickable: true,
      targetView: 'expiry'
    }
  ]


  const content = (
    <div className="space-y-6">

        {/* Content */}
        {activeView === 'overview' ? (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <Card
                  key={stat.label}
                  variant="neumorphic"
                  hover
                  className={stat.clickable ? 'cursor-pointer transition-transform hover:scale-105' : ''}
                  onClick={() => stat.clickable && handleStatClick(stat.targetView)}
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold text-brand-primary">
                      {stat.value}
                    </div>
                    <div className="text-sm text-brand-darkText mt-1">{stat.label}</div>
                    {stat.pastDue !== null && stat.pastDue > 0 && (
                      <div className="text-xs text-red-600 mt-2 font-medium">{t(stat.pastDueLabel, { count: stat.pastDue })}</div>
                    )}
                    {stat.change && (
                      <div className="text-xs text-green-600 mt-2 font-medium">{stat.change}</div>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="flex justify-center">
              <div className="w-full md:w-1/2">
                <Card variant="neumorphic">
                  <CardHeader>
                    <CardTitle>{t('quickActions.title')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Button
                        className="w-full justify-start bg-brand-primary hover:bg-red-700 text-white"
                        onClick={() => setActiveView('create')}
                        disabled={isCreating}
                      >
                        <span className="mr-3">📝</span>
                        {t('quickActions.createQuotation')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        ) : activeView === 'create' ? (
          <QuotationCreateView
            onSubmit={handleCreateQuotation}
            initialData={prefilledData}
          />
        ) : activeView === 'list' ? (
          <QuotesListView
            initialView={targetListView}
            onViewChange={() => setTargetListView(null)}
            preserveUrlParams={embedded}
            hideEmailFilter={hideEmailFilter}
          />
        ) : (
          <QuotesListView initialView={targetListView} onViewChange={() => setTargetListView(null)} />
        )}

        {/* Quotation Lines Modal - appears when selectedQuotationNumber is set */}
        {selectedQuotationNumber && (
          <QuotationLinesView
            quotationNumber={selectedQuotationNumber}
            startInEditMode
            onClose={() => setSelectedQuotationNumber(null)}
            onSave={() => {
              // Optionally refresh quotes list or show success message
              console.log('Quotation lines saved successfully')
            }}
          />
        )}
    </div>
  )

  if (embedded) {
    if (activeView === 'create') {
      // Created: show only this quotation's details (no way back to the list)
      if (selectedQuotationNumber) {
        return (
          <div className="min-h-screen bg-brand-contentBg">
            <QuotationLinesView
              quotationNumber={selectedQuotationNumber}
              standalone
              startInEditMode
              onClose={() => {}}
              onSave={() => console.log('Quotation lines saved successfully')}
            />
          </div>
        )
      }

      if (createdWithoutDetails) {
        return (
          <div className="min-h-screen bg-brand-contentBg p-4 sm:p-6 flex items-start justify-center">
            <div className="max-w-xl w-full mt-10 bg-white border border-green-200 rounded-lg shadow-sm p-6 text-center">
              <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xl">✓</div>
              <h2 className="text-lg font-semibold text-gray-800">{t('dashboard:embeddedCreate.createdNoDetailsTitle')}</h2>
              <p className="mt-2 text-sm text-gray-600">{t('dashboard:embeddedCreate.createdNoDetailsHint')}</p>
            </div>
          </div>
        )
      }

      return (
        <>
          <QuotationCreateView
            onSubmit={handleCreateQuotation}
            initialData={prefilledData}
          />
          {isCreating && (
            <div className="fixed inset-0 z-[10000] bg-white/80 backdrop-blur-sm flex items-center justify-center">
              <div className="flex flex-col items-center space-y-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                <p className="text-base font-medium text-gray-800">{t('dashboard:embeddedCreate.creatingTitle')}</p>
                <p className="text-sm text-gray-600">{t('dashboard:embeddedCreate.creatingHint')}</p>
              </div>
            </div>
          )}
        </>
      )
    }

    return (
      <div className="min-h-screen bg-brand-contentBg p-4 sm:p-6">
          <QuotesListView
            initialView={targetListView}
            onViewChange={() => setTargetListView(null)}
            preserveUrlParams
            hideEmailFilter={hideEmailFilter}
          />
      </div>
    )
  }

  return (
    <Layout activeView={activeView} setActiveView={setActiveView}>
      {content}
    </Layout>
  )
}
